/**
 * Parse Indian real-estate price + area strings into raw numbers so we can
 * derive ₹/sqft for side-by-side comparison in the Favourites diff table.
 *
 * Pure functions, no React deps — trivially unit-testable if we ever need it.
 */

// --- Price -----------------------------------------------------------------
// Accepts "₹50 Lakh", "50 lakh", "1.5 crore", "₹2 Cr", "5000000", "50L", "1.2Cr"
export function parsePriceToRupees(raw) {
  if (raw == null) return null;
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  const s = String(raw).toLowerCase().replace(/[₹,\s]/g, '');
  const m = s.match(/^([\d.]+)\s*(k|lakh|lac|l|crore|cr)?/);
  if (!m) return null;
  const val = parseFloat(m[1]);
  if (!Number.isFinite(val)) return null;
  const unit = m[2];
  if (!unit) return val;                  // plain rupees
  if (unit === 'k') return val * 1_000;
  if (unit === 'l' || unit === 'lakh' || unit === 'lac') return val * 100_000;
  if (unit === 'cr' || unit === 'crore') return val * 10_000_000;
  return val;
}

// --- Area ------------------------------------------------------------------
// Accepts "1200 sqft", "5 katha", "3 bigha_bengal", "2 acre", "500 sq yd",
// "5 gunta", "10 cent", "1 ground". Returns square-feet.
const UNIT_SQFT = {
  sqft: 1, sqfeet: 1, sf: 1,
  sqyd: 9, sqyard: 9, sy: 9, gaj: 9, sqyards: 9,
  sqm: 10.7639, sqmeter: 10.7639, sqmetre: 10.7639, sqmt: 10.7639,
  acre: 43560, acres: 43560,
  hectare: 107639, hectares: 107639,
  // Regional
  katha_bengal: 720, katha: 720,             // WB default
  katha_bihar: 1361.25,
  katha_assam: 2880,
  bigha_bengal: 14400, bigha: 14400,         // WB default
  bigha_up: 27225, bigha_uttarpradesh: 27225,
  bigha_rajasthan: 27225,
  bigha_mp: 12000,
  bigha_gujarat: 17427,
  ground: 2400, grounds: 2400,               // TN
  cent: 435.6, cents: 435.6,                 // Kerala/TN
  gunta: 1089, guntas: 1089,                 // MH/KA
  ares: 1076.39, are: 1076.39,
  marla: 272.25,                             // Punjab
  kanal: 5445,                               // Punjab
};

export function parseAreaToSqft(raw) {
  if (raw == null) return null;
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  const s = String(raw).toLowerCase().replace(/,/g, '').trim();
  const m = s.match(/([\d.]+)\s*([a-z_]+)?/);
  if (!m) return null;
  const val = parseFloat(m[1]);
  if (!Number.isFinite(val)) return null;
  const rawUnit = (m[2] || 'sqft').replace(/\s+/g, '').replace(/-/g, '_');
  // Normalise common variants
  const unit = rawUnit
    .replace(/^square/, 'sq')
    .replace('foot', 'ft')
    .replace('feet', 'ft')
    .replace('meter', 'm')
    .replace('metre', 'm')
    .replace('yard', 'yd');
  const factor = UNIT_SQFT[unit] ?? UNIT_SQFT[rawUnit] ?? UNIT_SQFT.sqft;
  return Math.round(val * factor);
}

// --- Formatters ------------------------------------------------------------
export function formatRupees(n) {
  if (n == null || !Number.isFinite(n)) return '—';
  if (n >= 1_00_00_000) return `₹${(n / 1_00_00_000).toFixed(2)} Cr`;
  if (n >= 1_00_000)   return `₹${(n / 1_00_000).toFixed(2)} L`;
  if (n >= 1_000)      return `₹${Math.round(n).toLocaleString('en-IN')}`;
  return `₹${n}`;
}

export function pricePerSqft(priceRaw, areaRaw) {
  const p = parsePriceToRupees(priceRaw);
  const a = parseAreaToSqft(areaRaw);
  if (!p || !a) return null;
  return Math.round(p / a);
}
